import {
  AUTH0_CIBA_SESSION_COOKIE,
  serverAuth0CibaSessionCookieAttrs,
} from '@/lib/auth0/ciba-session';
import {
  AUTH_PROVIDER_COOKIE,
  serverAuthProviderCookieAttrs,
} from '@/utils/auth/authProviderCookie';
import { type NextRequest, type NextResponse } from 'next/server';
import {
  NON_PERSISTENT_ALIVE_COOKIE,
  NON_PERSISTENT_ENFORCED_COOKIE,
  nonPersistentSessionCookieAttrs,
} from './cookies';

/**
 * The Auth0 SDK session cookie name.
 * `@auth0/nextjs-auth0` writes `__session` and chunked siblings
 * (`__session__0`, `__session__1`, …) for large payloads.
 *
 * The SDK is configured in `src/lib/auth0.ts` with
 * `session.cookie.domain = AUTH0_COOKIE_DOMAIN` whenever the env is set,
 * so in production the cookie is domain-scoped. Deletes must match the
 * same identity (name + path + domain) or the browser silently keeps
 * the original cookie alive.
 */
export const AUTH0_SESSION_COOKIE = '__session';

const auth0SessionCookieDomain = process.env.AUTH0_COOKIE_DOMAIN;

/**
 * Delete attributes for the Auth0 SDK session cookies (`__session` and
 * `__session__N` chunks). Mirrors the SDK setter in `src/lib/auth0.ts`:
 * path `/` always, domain only when `AUTH0_COOKIE_DOMAIN` is configured.
 */
const auth0SessionCookieDeleteAttrs = {
  path: '/',
  ...(auth0SessionCookieDomain && { domain: auth0SessionCookieDomain }),
  maxAge: 0,
} as const;

/**
 * Clears all Auth0 session cookies from a server response via `Set-Cookie`
 * headers. For use in Next.js middleware (Edge runtime) where the
 * browser-side `deleteCookie` helper is unavailable.
 *
 * Sets our custom auth-provider and non-persistent-enforced cookies to
 * empty with `maxAge: 0`, using the same identity (path + domain) they
 * were written with so the browser accepts each deletion. Also expires the
 * Auth0 SDK `__session` cookie and all `__session__N` chunked siblings
 * found in the incoming request.
 */
export function clearServerSideAuth0Cookies(
  response: NextResponse,
  req: NextRequest
): void {
  response.cookies.set(AUTH_PROVIDER_COOKIE, '', {
    ...serverAuthProviderCookieAttrs,
    maxAge: 0,
  });
  response.cookies.set(NON_PERSISTENT_ENFORCED_COOKIE, '', {
    ...nonPersistentSessionCookieAttrs,
    maxAge: 0,
  });
  response.cookies.set(NON_PERSISTENT_ALIVE_COOKIE, '', {
    ...nonPersistentSessionCookieAttrs,
    maxAge: 0,
  });
  response.cookies.set(AUTH0_CIBA_SESSION_COOKIE, '', {
    ...serverAuth0CibaSessionCookieAttrs,
    maxAge: 0,
  });
  response.cookies.set(AUTH0_SESSION_COOKIE, '', auth0SessionCookieDeleteAttrs);
  req.cookies.getAll().forEach((cookie) => {
    if (cookie.name.startsWith(`${AUTH0_SESSION_COOKIE}__`)) {
      response.cookies.set(cookie.name, '', auth0SessionCookieDeleteAttrs);
    }
  });
}
